"use client";

/**
 * Form controls shared by the free HAVS and noise exposure calculators.
 *
 * Inputs are type="text" with inputMode, not type="number": a number input
 * rejects a half-typed value such as "7." and snaps an emptied field to 0
 * (the F10 checker's snap-to-0 lesson). Everything here is local state only.
 */

import type { ReactNode } from "react";
import {
  durationInHours,
  parseNumberInput,
  sanitiseDecimalInput,
  sanitiseWholeInput,
} from "@/lib/marketing/exposure/format";

export const INPUT_CLASS =
  "mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 outline-none focus:border-accent focus:ring-2 focus:ring-accent/20";

export const SECONDARY_BUTTON_CLASS =
  "inline-flex min-h-[44px] items-center justify-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50";

/**
 * Each calculator's whole region is excluded from PostHog autocapture and
 * session replay (`ph-no-capture`; `ph-mask` is the replay maskTextSelector in
 * components/analytics/posthog-provider.tsx) and carries
 * `data-clarity-mask="true"` for Clarity, so the results, which repeat what the
 * visitor typed, never reach a recording. Same convention as the H&S policy
 * generator. Pinned by __tests__/marketing/exposure-calculators-privacy.test.tsx.
 */
export const PRIVATE_REGION_CLASS = "ph-no-capture ph-mask";

/** Most rows a calculator holds (HSE's own spreadsheet takes six). */
export const MAX_ROWS = 8;
/** Longest tool or activity name, in characters. */
export const MAX_NAME_LENGTH = 60;

export interface ExposureRow {
  id: number;
  name: string;
  /** Vibration magnitude (m/s²) or noise level (dB(A)), as typed. */
  value: string;
  hoursText: string;
  minutesText: string;
}

export type ExposureRowField = "name" | "value" | "hoursText" | "minutesText";

export function sanitiseRowField(field: ExposureRowField, raw: string): string {
  if (field === "name") return raw.slice(0, MAX_NAME_LENGTH);
  if (field === "value" || field === "hoursText") return sanitiseDecimalInput(raw);
  return sanitiseWholeInput(raw);
}

export interface ParsedRow {
  /** The visitor has typed something in this row (an untouched row is ignored). */
  started: boolean;
  /** The row has a value and a time above zero, and no errors. */
  complete: boolean;
  value: number;
  hours: number;
  error: string | null;
}

/**
 * Reads one row. `maxValue` bounds the magnitude or level; `valueLimitText`
 * names it in the error ("a vibration magnitude of 100 m/s²").
 */
export function parseRow(row: ExposureRow, maxValue: number, valueLimitText: string): ParsedRow {
  const value = parseNumberInput(row.value);
  const minutes = parseNumberInput(row.minutesText);
  const hours = durationInHours(row.hoursText, row.minutesText);
  let error: string | null = null;
  if (value !== null && value > maxValue) error = `Enter ${valueLimitText} or less.`;
  else if (minutes !== null && minutes > 59) error = "Enter minutes from 0 to 59.";
  else if (hours > 24) error = "Enter a time of 24 hours or less.";
  return {
    started: [row.name, row.value, row.hoursText, row.minutesText].some((field) => field.trim() !== ""),
    complete: error === null && value !== null && value > 0 && hours > 0,
    value: value ?? 0,
    hours,
    error,
  };
}

/**
 * Why the day's total cannot be shown yet, or null when it can. A row with an
 * error, or a started row missing its value or time, is left out of the
 * arithmetic, so a total beside it would understate the day: the calculators
 * withhold the total and status until every started row is complete or
 * removed. Untouched empty rows are ignored.
 */
export function totalBlocker(rows: readonly ParsedRow[]): "error" | "incomplete" | null {
  if (rows.some((row) => row.error !== null)) return "error";
  if (rows.some((row) => row.started && !row.complete)) return "incomplete";
  return null;
}

export function TextField({
  label,
  value,
  onChange,
  inputMode,
  placeholder,
  maxLength,
  describedBy,
  invalid,
}: {
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  inputMode?: "decimal" | "numeric";
  placeholder?: string;
  maxLength?: number;
  describedBy?: string;
  invalid?: boolean;
}) {
  return (
    <label className="block text-sm font-medium text-gray-800">
      {label}
      <input
        type="text"
        inputMode={inputMode}
        autoComplete="off"
        spellCheck={false}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        onChange={(event) => onChange(event.target.value)}
        className={INPUT_CLASS}
      />
    </label>
  );
}

/** Tone classes for a result status, from the design system's status hues. */
export const STATUS_TONE_CLASS: Record<"done" | "attn" | "danger", string> = {
  done: "border-done-line bg-done-bg text-done",
  attn: "border-attn-line bg-attn-bg text-attn",
  danger: "border-danger-line bg-danger-bg text-danger",
};
